import { motion, useInView } from 'framer-motion';
import { useRef } from 'react';

const EvolutionSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-100px' });

  const developments = [
    'A global idea-generation architecture designed to produce cross-sector breakthrough concepts and scalable frameworks',
    'High-intelligence AI minds engineered to assist in civilisational design and complex strategic synthesis',
    'Cultural renaissance frameworks that elevate human creativity, unity, and collective meaning',
    'Consciousness expansion systems aligned with next-era human potential and multi-domain integration',
    'Civilisational protocols that redefine how nations, societies, and ecosystems interact and co-evolve'
  ];

  return (
    <section 
      id="evolution" 
      className="py-24 md:py-32 relative" 
      ref={ref} 
      data-testid="evolution-section"
      style={{ background: '#050505' }}
    >
      <div className="max-w-4xl mx-auto px-6 md:px-12">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
          className="mb-16 text-center"
        >
          <h2 
            className="text-3xl md:text-4xl lg:text-5xl font-light tracking-[0.2em] text-white mb-4"
            style={{ fontFamily: "'Cinzel', serif" }}
          >
            Evolution
          </h2>
          <div className="w-24 h-[1px] bg-[#c9a227]/50 mx-auto" />
        </motion.div>

        {/* Where we are going */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
          className="mb-16 text-center"
        >
          <h3 
            className="text-xl md:text-2xl font-light tracking-wider text-[#c9a227] mb-6"
            style={{ fontFamily: "'Cinzel', serif" }}
          >
            Where we are going
          </h3>
          <p className="text-white/70 leading-relaxed max-w-3xl mx-auto">
            The evolution of Celestial Minds™ is the evolution of human possibility — advancing the underlying architectures that shape societies, systems, and global trajectories.
          </p>
        </motion.div>

        <div className="w-full h-[1px] bg-gradient-to-r from-transparent via-[#c9a227]/30 to-transparent mb-16" />

        {/* We are developing */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
          className="mb-16"
        >
          <h3 
            className="text-xl md:text-2xl font-light tracking-wider text-[#c9a227] mb-8 text-center"
            style={{ fontFamily: "'Cinzel', serif" }}
          >
            We are developing
          </h3>
          <ul className="space-y-4">
            {developments.map((item, index) => (
              <motion.li
                key={index}
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.4, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="flex items-start gap-4 text-white/70 leading-relaxed"
              >
                <span className="text-[#c9a227] mt-1 flex-shrink-0">•</span>
                <span>{item}</span>
              </motion.li>
            ))}
          </ul>
          <p className="text-white/60 mt-8 text-center leading-relaxed">
            Celestial Minds™ is constructing the blueprint for the next chapter of human civilisation — grounded in structured insight, systemic coherence, and strategic depth.
          </p>
        </motion.div>

        <div className="w-full h-[1px] bg-gradient-to-r from-transparent via-[#c9a227]/30 to-transparent mb-16" />

        {/* The future is a choice */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
          className="text-center"
        >
          <h3 
            className="text-xl md:text-2xl font-light tracking-wider text-[#c9a227] mb-6"
            style={{ fontFamily: "'Cinzel', serif" }}
          >
            The future is a choice
          </h3>
          <div className="space-y-6 text-white/70 leading-relaxed max-w-3xl mx-auto">
            <p>
              The path ahead is shaped by design, not default.
            </p>
            <p>
              Celestial Minds™ advances this work one idea, one architecture, one breakthrough at a time. Those who engage with us do not merely witness the future — they contribute to its definition. And this is only the beginning.
            </p>
          </div>
          <blockquote 
            className="mt-10 text-lg md:text-xl italic text-[#c9a227]"
            style={{ fontFamily: "'Cormorant Garamond', serif" }}
          >
            "Celestial Minds™ envisions a future in which systemic architecture, human potential, and multidisciplinary intelligence converge to elevate global capability."
          </blockquote>
        </motion.div>
      </div>
    </section>
  );
};

export default EvolutionSection;
